import React from "react";
import { clearAllListCaches } from "../redesign/listCache";
import { useLocation } from "react-router-dom";
import { Building2, Car } from "lucide-react";
import { CLIENTS } from "../config/clients";
import { getSourceName, getUserProfile, setSourceName } from "../services/authService";
import { lineOptions } from "../utils/lineSwitch";

/**
 * Motor or property, for an Ergo admin, without signing out.
 *
 * The two lines are separate deployments — their own hosts, source names, media
 * servers and case-detail pages — so switching is a change of client, not a
 * filter. Which is why it reloads: host, source_name, media base and detail
 * page are all read from the session at render time, and half the app holds
 * state fetched under the previous line. A reload is one honest line where
 * chasing every reader would be many fragile ones.
 *
 * Renders nothing unless there is a choice to make: only Ergo has two lines,
 * and only an access level that is not itself line-scoped may cross between
 * them. See utils/lineSwitch.js for why that is level 10.
 *
 * @param {{variant?: "rail"|"icon"}} props
 */
export default function LineSwitcher({ variant = "rail" }) {
  const { pathname } = useLocation();

  const current = getSourceName();
  const options = lineOptions(current, CLIENTS, getUserProfile());
  if (options.length < 2) return null;

  const onRedesign = pathname.startsWith("/redesign/");

  const choose = (key) => {
    if (key === current) return;
    // The other line is a different backend with different rows; a cached
    // list keyed to this one must not be restored over there.
    clearAllListCaches();
    setSourceName(key);
    // The list, not the case that was open: a motor ref_num means nothing to
    // the property backend, and vice versa.
    window.location.assign(onRedesign ? "/redesign/cases" : "/cases");
  };

  if (variant === "icon") {
    const other = options.find((option) => !option.current);
    const Icon = other.line === "property" ? Building2 : Car;
    return (
      <button
        type="button"
        onClick={() => choose(other.key)}
        title={`Switch to ${other.label.toLowerCase()} cases`}
        className="flex w-full flex-col items-center gap-1.5 rounded-2xl py-2"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl">
          <Icon className="h-8 w-8 text-text-secondary" />
        </div>
        <span className="text-center text-xs font-bold uppercase leading-none tracking-tight text-text-secondary">
          {other.label}
        </span>
      </button>
    );
  }

  return (
    <div>
      <div className="px-[10px] pb-[5px] text-[10px] font-semibold tracking-[0.06em] text-cl-label">
        LINE
      </div>
      <div
        role="group"
        aria-label="Line of business"
        className="flex gap-[2px] rounded-tile bg-cl-bg p-[2px]"
      >
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => choose(option.key)}
            aria-pressed={option.current}
            className={`flex-1 rounded-[6px] px-[8px] py-[5px] text-[12px] font-semibold transition-colors ${
              option.current
                ? "bg-cl-surface text-cl-accent shadow-[0_1px_2px_rgba(19,23,32,0.08)]"
                : "text-cl-faint hover:text-cl-secondary"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}
